import React, {useState} from 'react';
import moment from 'moment';
import groupBy from 'lodash/groupBy';

import Box from '@mui/material/Box';
import SearchIcon from '@mui/icons-material/Search';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
// import FilterIcon from '@mui/icons-material/FilterList';

import {
  useGetProviderAvailabilitiesQuery
} from '../../redux/services/providerAvailabilityApi';
import useDebouncedState from '../../hooks/useDebouncedState';

import CustomTextField from '../common/CustomTextField';
import DayPicker from '../common/DayPicker';
import SchedulingProviderItem from './SchedulingProviderItem';

const parseQuery = ({
  searchMethod, 
  location, 
  serviceCategory, 
  date,
  searchText,
}) => {
  if (searchMethod === 'BY_PROVIDER') {
    return {
      lat: location.lat,
      lon: location.lon,
      distance: location.distance || 30,
      service_category: serviceCategory,
      search_text: searchText,
    };
  }
  const dt = moment(date);
  return {
    lat: location.lat,
    lon: location.lon, 
    distance: location.distance || 30,
    service_category: serviceCategory,
    day: dt.format('ddd').toUpperCase(),
    start_minute__gte: 1,
    end_minute__lte: 23 * 60 + 59,
  };
};

const PatientSchedulingProviderForm = ({
  onSubmit,
  searchMethod = 'BY_AVAILABILITY',
  location = null,
  serviceCategory = null,
  stepNum = 3,
}) => {
  const [searchText, setSearchText] = useState('');
  const [date, setDate] = useState();
  useDebouncedState(searchText, setSearchText, 500);
  const title = searchMethod === 'BY_PROVIDER' ? 'Find a Provider' : 'Pick a Time';
  const subtitle = (
    searchMethod === 'BY_PROVIDER' 
    ? 'Find a provider who fits your needs' 
    : 'Find a provider who fits your schedule'
  );
  const query = parseQuery({
    searchMethod, 
    location, 
    serviceCategory, 
    date, 
    searchText,
  });
  const paQuery = useGetProviderAvailabilitiesQuery(
    query, 
    {skip: searchMethod === 'BY_AVAILABILITY' && !date}
  );
  const availabilities = paQuery?.data?.data || [];
  const providers = Object.entries(groupBy(availabilities, 'provider.id'))
    .map(([k, v]) => ({...v[0].provider, availabilities: v}));
  return (
    <Box>
      <Stack spacing={2} alignItems='center'>
        <Stack spacing={0.5} alignItems='center'>
          <Typography variant='h2'>Step {stepNum + 1}: {title}</Typography>
          <Typography>{subtitle}</Typography>
        </Stack>
        {searchMethod === 'BY_AVAILABILITY' && 
          <>
            <Stack 
              direction='row' 
              alignItems='center' 
              justifyContent='space-between'
            >
              <Typography variant='h3'>
                Pick a Day to View Available Appointments
              </Typography>

            </Stack>
            <DayPicker 
              date={date} 
              spacing={0.5}
              onChange={(e, date) => setDate(date)}
              availabilities={availabilities} 
              disabledDays={['SAT', 'SUN']}
              allowPastDays={false}
            />
          </>
        }
        {
          searchMethod === 'BY_PROVIDER' && 
          <>
            <Stack direction='row' alignItems='center' spacing={1}>
              <Typography variant='h3'>Providers Near You</Typography>
            </Stack>
            <Stack direction='row' alignItems='center' spacing={1}>
              <CustomTextField
                placeholder='Search by name, specialty, or condition'
                fullWidth
                style={{minWidth: '400px'}}
                InputProps={{
                  startAdornment: <SearchIcon color='primary' />
                }}
                onChange={(e, val) => setSearchText(val)}
              />
              {/* <Button
                color='light'
                variant='contained'
                size='small'
                startIcon={<FilterIcon />}
              >
                Filter
              </Button> */}
            </Stack>
          </>
        }
        <Stack spacing={2} padding={2} style={{width: '100%'}}>
          {
            providers.map(p => 
              <SchedulingProviderItem 
                key={p.id} 
                provider={p}
                availabilities={p.availabilities}
                serviceCategory={serviceCategory}
                location={location}
                day={moment(date).format('ddd').toUpperCase()}
                date={date}
                onTimeSelect={(e, data) => onSubmit(e, data)}
                hideSlots={searchMethod === 'BY_PROVIDER'}
              />
            )
          }
          {
            paQuery.isSuccess && providers.length === 0 &&
            <Typography variant='h3' align='center' color='text.muted' py={4}>
              No providers found. Try expanding your search criteria.
            </Typography>
          }
        </Stack>
      </Stack>
    </Box>
  );
};

export default PatientSchedulingProviderForm;